'use client'

import { cn } from '@/ui/utils/style'
import { Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

interface ExpandableSearchProps {
  value: string
  onChange: (value: string) => void
  onClear?: () => void
  placeholder?: string
  className?: string
  size?: 'sm' | 'default'
}

export function ExpandableSearch({
  value,
  onChange,
  onClear,
  placeholder = 'Search...',
  className,
  size = 'default',
}: ExpandableSearchProps) {
  const [expanded, setExpanded] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const hasActiveSearch = value.length > 0
  const isOpen = expanded || hasActiveSearch

  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus()
    }
  }, [expanded])

  const handleClear = () => {
    onChange('')
    setExpanded(false)
    onClear?.()
  }

  const handleToggle = () => {
    if (isOpen && !hasActiveSearch) {
      setExpanded(false)
    } else if (!isOpen) {
      setExpanded(true)
    }
  }

  const isSm = size === 'sm'

  return (
    <div
      className={cn(
        'flex items-center overflow-hidden rounded-lg border border-white/10 bg-white/5 transition-[width] duration-200',
        isSm ? 'h-8' : 'h-9',
        isOpen ? (isSm ? 'w-44' : 'w-48') : isSm ? 'w-8' : 'w-9',
        className,
      )}
    >
      <button
        type="button"
        aria-label="Open search"
        onClick={handleToggle}
        className={cn('flex shrink-0 items-center justify-center text-muted-foreground', isSm ? 'w-8' : 'w-9')}
      >
        <Search className={isSm ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      </button>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => !value && setExpanded(false)}
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent text-foreground transition-opacity duration-150 placeholder:text-muted-foreground focus:outline-none',
          isSm ? 'text-xs' : 'text-sm',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        tabIndex={isOpen ? 0 : -1}
      />
      <button
        type="button"
        aria-label="Clear search"
        onClick={handleClear}
        className={cn(
          'flex shrink-0 items-center justify-center text-muted-foreground transition-opacity duration-150 hover:text-foreground',
          isSm ? 'pr-2' : 'pr-2.5',
          hasActiveSearch ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        tabIndex={hasActiveSearch ? 0 : -1}
      >
        <X className={isSm ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
      </button>
    </div>
  )
}
